import React, { useContext, useEffect, useRef } from 'react';
import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { EditOfferPageContext } from '@bbkAdminComponents/experiences/offers/edit-offer-page-context';
import {
  type HeroImageV1,
  type RestLCOBanner,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { CloseOfferButton } from '@bbkFunnelComponents/modals/sections';
import { RefsContext } from '@bbkFunnelUtils/refs.context';

import './modal.scss';

type Props = {
  modalInfo: RestLCOBanner;
  detailedAction: React.ReactElement;
  close: () => void;
  isPreview: boolean;
};

const CopyContent = styled.div`
  overflow-y: auto;
  align-content: center;
  padding-top: 1rem;
  padding-bottom: 1rem;
  padding-right: 1.5rem;
  padding-left: 1.5rem;
  max-width: 60%;
  max-height: 70px;
  box-sizing: content-box;
`;

export const LCOBanner: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      hero,
      info: { details },
    },
  },
  detailedAction,
  close,
  isPreview,
}) => {
  const theme = useTheme() as EmotionTheme;
  const customCardTheme = theme.cardLevelTheme;
  const offerEditorPreview = !!useContext(EditOfferPageContext);
  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const imageRef = useRef<HTMLImageElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-image`, imageRef), [imageRef]);
  const descRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-desc`, descRef), [descRef]);
  const actionsRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-actions`, actionsRef),
    [actionsRef]
  );
  const imgUrl = (hero as HeroImageV1).url;

  const copy = <CustomerHtml value={details} />;

  const customContainerStyle = customCardTheme?.useCustom
    ? {
        backgroundColor: customCardTheme.style.backgroundColor,
        color: customCardTheme.style.textColor,
      }
    : {};

  return (
    <div
      className={cs('banner', 'desktop')}
      style={{
        ...customContainerStyle,
        ...(isPreview
          ? offerEditorPreview
            ? {
                width: '1440px',
              }
            : {
                width: '1440px',
                transform: 'scale(0.6)',
                transformOrigin: 'top left',
              }
          : { width: 'auto' }),
      }}
    >
      <div className="banner-container">
        <img
          ref={imageRef}
          alt=""
          src={imgUrl}
          css={css`
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            max-width: 150px;
            margin-right: 1.5rem;
          `}
        />
        <CopyContent className="banner-text">
          <div ref={descRef}>{copy}</div>
        </CopyContent>
        <div
          css={css`
            padding-left: 1rem;
          `}
          ref={actionsRef}
        >
          {detailedAction}
        </div>
      </div>
      <div
        css={css`
          margin-top: auto;
          margin-bottom: auto;
          display: flex;
          justify-content: center;
        `}
      >
        <CloseOfferButton
          newVariant
          onClick={close}
          css={css`
            margin-right: 2rem;
          `}
        />
      </div>
    </div>
  );
};
